import type React from 'react';
import type { IntlProvider, MessageFormatElement } from 'react-intl';

type IntlProviderProps = React.ComponentProps<typeof IntlProvider>;

export const defaultRichTextElements: IntlProviderProps['defaultRichTextElements'] =
  {
    b: (chunks) => <b>{chunks}</b>,
  };

export type AvailableLocale =
  | 'af'
  | 'am'
  | 'ar'
  | 'az'
  | 'be'
  | 'bg'
  | 'bn'
  | 'bs'
  | 'ca'
  | 'cs'
  | 'da'
  | 'de'
  | 'el'
  | 'en-GB'
  | 'en-US'
  | 'es'
  | 'es-MX'
  | 'et'
  | 'fa-AF'
  | 'fa-IR'
  | 'fi'
  | 'fr'
  | 'fr-CA'
  | 'gu'
  | 'ha'
  | 'he'
  | 'hi-IN'
  | 'hr'
  | 'ht'
  | 'hu'
  | 'hy'
  | 'id'
  | 'is'
  | 'it'
  | 'ja'
  | 'ka'
  | 'kk'
  | 'kn'
  | 'ko'
  | 'lb'
  | 'lt'
  | 'lv'
  | 'mk'
  | 'ml'
  | 'mn'
  | 'mr'
  | 'ms'
  | 'nl'
  | 'no'
  | 'pa'
  | 'pl'
  | 'ps'
  | 'pt'
  | 'pt-PT'
  | 'ro'
  | 'ru'
  | 'si'
  | 'sk'
  | 'sl'
  | 'so'
  | 'sq'
  | 'sr'
  | 'sv'
  | 'sw'
  | 'ta'
  | 'te'
  | 'th'
  | 'tl'
  | 'tr'
  | 'uk'
  | 'ur'
  | 'uz'
  | 'vi'
  | 'zh-CN'
  | 'zh-HK'
  | 'zh-TW';

/**
 * Maps browser locales, and the spellings Hire stores in a user's profile, to
 * the locale Hire Go renders. {@link resolveLocale} reads it.
 */
export const LOCALE_MAPPING: Record<string, AvailableLocale> = {
  // Afrikaans
  af: 'af',
  'af-NA': 'af',
  'af-ZA': 'af',

  // Amharic
  am: 'am',
  'am-ET': 'am',

  // Arabic
  ar: 'ar',
  'ar-001': 'ar',
  'ar-AE': 'ar',
  'ar-BH': 'ar',
  'ar-DJ': 'ar',
  'ar-DZ': 'ar',
  'ar-EG': 'ar',
  'ar-EH': 'ar',
  'ar-ER': 'ar',
  'ar-IL': 'ar',
  'ar-IQ': 'ar',
  'ar-JO': 'ar',
  'ar-KM': 'ar',
  'ar-KW': 'ar',
  'ar-LB': 'ar',
  'ar-LY': 'ar',
  'ar-MA': 'ar',
  'ar-MR': 'ar',
  'ar-OM': 'ar',
  'ar-PS': 'ar',
  'ar-QA': 'ar',
  'ar-SA': 'ar',
  'ar-SD': 'ar',
  'ar-SO': 'ar',
  'ar-SS': 'ar',
  'ar-SY': 'ar',
  'ar-TD': 'ar',
  'ar-TN': 'ar',
  'ar-YE': 'ar',

  // Azerbaijani
  az: 'az',
  'az-AZ': 'az',
  'az-Latn': 'az',
  'az-Latn-AZ': 'az',

  // Belarusian
  be: 'be',
  'be-BY': 'be',

  // Bulgarian
  bg: 'bg',
  'bg-BG': 'bg',

  // Bengali
  bn: 'bn',
  'bn-BD': 'bn',
  'bn-IN': 'bn',

  // Bosnian
  bs: 'bs',
  'bs-BA': 'bs',
  'bs-Cyrl': 'bs',
  'bs-Cyrl-BA': 'bs',
  'bs-Latn': 'bs',
  'bs-Latn-BA': 'bs',

  // Catalan
  ca: 'ca',
  'ca-AD': 'ca',
  'ca-ES': 'ca',
  'ca-FR': 'ca',
  'ca-IT': 'ca',

  // Czech
  cs: 'cs',
  'cs-CZ': 'cs',

  // Danish
  da: 'da',
  'da-DK': 'da',
  'da-GL': 'da',

  // German
  de: 'de',
  'de-AT': 'de',
  'de-BE': 'de',
  'de-CH': 'de',
  'de-DE': 'de',
  'de-IT': 'de',
  'de-LI': 'de',
  'de-LU': 'de',

  // Greek
  el: 'el',
  'el-CY': 'el',
  'el-GR': 'el',

  // English
  en: 'en-US',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  // All other English variants default to en-US
  'en-001': 'en-US',
  'en-150': 'en-US',
  'en-AG': 'en-US',
  'en-AI': 'en-US',
  'en-AS': 'en-US',
  'en-AT': 'en-US',
  'en-AU': 'en-US',
  'en-BB': 'en-US',

  // Spanish
  es: 'es',
  'es-419': 'es',
  'es-AR': 'es',
  'es-BO': 'es',
  'es-BR': 'es',
  'es-BZ': 'es',
  'es-CL': 'es',
  'es-CO': 'es',
  'es-CR': 'es',
  'es-CU': 'es',
  'es-DO': 'es',
  'es-EA': 'es',
  'es-EC': 'es',
  'es-ES': 'es',
  'es-GQ': 'es',
  'es-GT': 'es',
  'es-HN': 'es',
  'es-IC': 'es',
  'es-MX': 'es-MX',
  'es-NI': 'es',
  'es-PA': 'es',
  'es-PE': 'es',
  'es-PH': 'es',
  'es-PR': 'es',
  'es-PY': 'es',
  'es-SV': 'es',
  'es-US': 'es',
  'es-UY': 'es',
  'es-VE': 'es',

  // Estonian
  et: 'et',
  'et-EE': 'et',

  // Persian. Dari is Afghanistan's Persian; every other region reads Farsi.
  fa: 'fa-IR',
  'fa-AF': 'fa-AF',
  'fa-IR': 'fa-IR',
  prs: 'fa-AF',
  'prs-AF': 'fa-AF',

  // Finnish
  fi: 'fi',
  'fi-FI': 'fi',

  // French
  fr: 'fr',
  'fr-BE': 'fr',
  'fr-BF': 'fr',
  'fr-BI': 'fr',
  'fr-BJ': 'fr',
  'fr-BL': 'fr',
  'fr-CA': 'fr-CA',
  'fr-CD': 'fr',
  'fr-CF': 'fr',
  'fr-CG': 'fr',
  'fr-CH': 'fr',
  'fr-CI': 'fr',
  'fr-CM': 'fr',
  'fr-DJ': 'fr',
  'fr-DZ': 'fr',
  'fr-FR': 'fr',
  'fr-GA': 'fr',
  'fr-GF': 'fr',
  'fr-GN': 'fr',
  'fr-GP': 'fr',
  'fr-GQ': 'fr',
  'fr-HT': 'fr',
  'fr-KM': 'fr',
  'fr-LU': 'fr',
  'fr-MA': 'fr',
  'fr-MC': 'fr',
  'fr-MF': 'fr',
  'fr-MG': 'fr',
  'fr-ML': 'fr',
  'fr-MQ': 'fr',
  'fr-MR': 'fr',
  'fr-MU': 'fr',
  'fr-NC': 'fr',
  'fr-NE': 'fr',
  'fr-PF': 'fr',
  'fr-PM': 'fr',
  'fr-RE': 'fr',
  'fr-RW': 'fr',
  'fr-SC': 'fr',
  'fr-SN': 'fr',
  'fr-SY': 'fr',
  'fr-TD': 'fr',
  'fr-TG': 'fr',
  'fr-TN': 'fr',
  'fr-VU': 'fr',
  'fr-WF': 'fr',
  'fr-YT': 'fr',

  // Gujarati
  gu: 'gu',
  'gu-IN': 'gu',

  // Hausa
  ha: 'ha',
  'ha-GH': 'ha',
  'ha-NE': 'ha',
  'ha-NG': 'ha',

  // Hebrew
  he: 'he',
  'he-IL': 'he',

  // Hindi
  hi: 'hi-IN',
  'hi-IN': 'hi-IN',

  // Croatian
  hr: 'hr',
  'hr-BA': 'hr',
  'hr-HR': 'hr',

  // Haitian Creole
  ht: 'ht',
  'ht-HT': 'ht',

  // Hungarian
  hu: 'hu',
  'hu-HU': 'hu',

  // Armenian
  hy: 'hy',
  'hy-AM': 'hy',

  // Indonesian
  id: 'id',
  'id-ID': 'id',

  // Icelandic
  is: 'is',
  'is-IS': 'is',

  // Italian
  it: 'it',
  'it-CH': 'it',
  'it-IT': 'it',
  'it-SM': 'it',
  'it-VA': 'it',

  // Japanese
  ja: 'ja',
  'ja-JP': 'ja',

  // Georgian
  ka: 'ka',
  'ka-GE': 'ka',

  // Kazakh
  kk: 'kk',
  'kk-KZ': 'kk',

  // Kannada
  kn: 'kn',
  'kn-IN': 'kn',

  // Korean
  ko: 'ko',
  'ko-KP': 'ko',
  'ko-KR': 'ko',

  // Luxembourgish
  lb: 'lb',
  'lb-LU': 'lb',

  // Lithuanian
  lt: 'lt',
  'lt-LT': 'lt',

  // Latvian
  lv: 'lv',
  'lv-LV': 'lv',

  // Macedonian
  mk: 'mk',
  'mk-MK': 'mk',

  // Malayalam
  ml: 'ml',
  'ml-IN': 'ml',

  // Mongolian
  mn: 'mn',
  'mn-MN': 'mn',

  // Marathi
  mr: 'mr',
  'mr-IN': 'mr',

  // Malay
  ms: 'ms',
  'ms-BN': 'ms',
  'ms-MY': 'ms',
  'ms-SG': 'ms',

  // Dutch
  nl: 'nl',
  'nl-AW': 'nl',
  'nl-BE': 'nl',
  'nl-BQ': 'nl',
  'nl-CW': 'nl',
  'nl-NL': 'nl',
  'nl-SR': 'nl',
  'nl-SX': 'nl',

  // Norwegian
  no: 'no',
  'nb-NO': 'no',
  'nb-SJ': 'no',
  'nn-NO': 'no',
  'no-NO': 'no',

  // Punjabi
  pa: 'pa',
  'pa-Guru': 'pa',
  'pa-Guru-IN': 'pa',
  'pa-IN': 'pa',

  // Polish
  pl: 'pl',
  'pl-PL': 'pl',

  // Pashto
  ps: 'ps',
  'ps-AF': 'ps',
  'ps-PK': 'ps',

  // Portuguese
  pt: 'pt',
  'pt-AO': 'pt',
  'pt-BR': 'pt',
  'pt-CH': 'pt',
  'pt-CV': 'pt',
  'pt-GQ': 'pt',
  'pt-GW': 'pt',
  'pt-LU': 'pt',
  'pt-MO': 'pt',
  'pt-MZ': 'pt',
  'pt-PT': 'pt-PT',
  'pt-ST': 'pt',
  'pt-TL': 'pt',

  // Romanian
  ro: 'ro',
  'ro-MD': 'ro',
  'ro-RO': 'ro',

  // Russian
  ru: 'ru',
  'ru-BY': 'ru',
  'ru-KG': 'ru',
  'ru-KZ': 'ru',
  'ru-MD': 'ru',
  'ru-RU': 'ru',
  'ru-UA': 'ru',

  // Sinhala
  si: 'si',
  'si-LK': 'si',

  // Slovak
  sk: 'sk',
  'sk-SK': 'sk',

  // Slovenian
  sl: 'sl',
  'sl-SI': 'sl',

  // Somali
  so: 'so',
  'so-DJ': 'so',
  'so-ET': 'so',
  'so-KE': 'so',
  'so-SO': 'so',

  // Albanian
  sq: 'sq',
  'sq-AL': 'sq',
  'sq-MK': 'sq',
  'sq-XK': 'sq',

  // Serbian
  sr: 'sr',
  'sr-Cyrl': 'sr',
  'sr-Cyrl-RS': 'sr',
  'sr-Latn': 'sr',
  'sr-Latn-RS': 'sr',
  'sr-RS': 'sr',

  // Swedish
  sv: 'sv',
  'sv-AX': 'sv',
  'sv-FI': 'sv',
  'sv-SE': 'sv',

  // Swahili
  sw: 'sw',
  'sw-CD': 'sw',
  'sw-KE': 'sw',
  'sw-TZ': 'sw',
  'sw-UG': 'sw',

  // Tamil
  ta: 'ta',
  'ta-IN': 'ta',
  'ta-LK': 'ta',
  'ta-MY': 'ta',
  'ta-SG': 'ta',

  // Telugu
  te: 'te',
  'te-IN': 'te',

  // Thai
  th: 'th',
  'th-TH': 'th',

  // Tagalog
  tl: 'tl',
  'fil-PH': 'tl',
  'tl-PH': 'tl',

  // Turkish
  tr: 'tr',
  'tr-CY': 'tr',
  'tr-TR': 'tr',

  // Ukrainian
  uk: 'uk',
  'uk-UA': 'uk',

  // Urdu
  ur: 'ur',
  'ur-IN': 'ur',
  'ur-PK': 'ur',

  // Uzbek
  uz: 'uz',
  'uz-Latn': 'uz',
  'uz-Latn-UZ': 'uz',
  'uz-UZ': 'uz',

  // Vietnamese
  vi: 'vi',
  'vi-VN': 'vi',

  // Chinese. `tw` is how Hire stores Traditional Chinese (Taiwan).
  tw: 'zh-TW',
  zh: 'zh-CN',
  'zh-CN': 'zh-CN',
  'zh-HK': 'zh-HK',
  'zh-Hans': 'zh-CN',
  'zh-Hans-CN': 'zh-CN',
  'zh-Hans-SG': 'zh-CN',
  'zh-Hant': 'zh-TW',
  'zh-Hant-HK': 'zh-HK',
  'zh-Hant-MO': 'zh-HK',
  'zh-Hant-TW': 'zh-TW',
  'zh-MO': 'zh-HK',
  'zh-SG': 'zh-CN',
  'zh-TW': 'zh-TW',
};

export const AVAILABLE_LOCALES: AvailableLocale[] = [
  'af',
  'am',
  'ar',
  'az',
  'be',
  'bg',
  'bn',
  'bs',
  'ca',
  'cs',
  'da',
  'de',
  'el',
  'en-GB',
  'en-US',
  'es',
  'es-MX',
  'et',
  'fa-AF',
  'fa-IR',
  'fi',
  'fr',
  'fr-CA',
  'gu',
  'ha',
  'he',
  'hi-IN',
  'hr',
  'ht',
  'hu',
  'hy',
  'id',
  'is',
  'it',
  'ja',
  'ka',
  'kk',
  'kn',
  'ko',
  'lb',
  'lt',
  'lv',
  'mk',
  'ml',
  'mn',
  'mr',
  'ms',
  'nl',
  'no',
  'pa',
  'pl',
  'ps',
  'pt',
  'pt-PT',
  'ro',
  'ru',
  'si',
  'sk',
  'sl',
  'so',
  'sq',
  'sr',
  'sv',
  'sw',
  'ta',
  'te',
  'th',
  'tl',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zh-CN',
  'zh-HK',
  'zh-TW',
];

const FALLBACK_LOCALE: AvailableLocale = 'en-US';

const LOCALE_LOOKUP = new Map(
  Object.entries(LOCALE_MAPPING).map(([code, locale]) => [
    code.toLowerCase(),
    locale,
  ])
);

const RIGHT_TO_LEFT_LOCALES: ReadonlySet<AvailableLocale> = new Set([
  'ar',
  'fa-AF',
  'fa-IR',
  'he',
  'ps',
  'ur',
]);

/**
 * The locale Hire Go renders for a stored or browser code. Hire spells a
 * profile's language several ways (`ko`, `af-ZA`, `hi-in`, `tw`), so the code
 * is matched without case, then by its base language, then falls back to en-US.
 */
export function resolveLocale(
  code: string | null | undefined
): AvailableLocale {
  if (!code) {
    return FALLBACK_LOCALE;
  }

  const written = code.replaceAll('_', '-').toLowerCase();
  const baseLanguage = written.split('-')[0];
  return (
    LOCALE_LOOKUP.get(written) ??
    LOCALE_LOOKUP.get(baseLanguage) ??
    FALLBACK_LOCALE
  );
}

export function textDirection(locale: AvailableLocale): 'ltr' | 'rtl' {
  return RIGHT_TO_LEFT_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

/**
 * Body of the inline script that sets the page's language and direction ahead
 * of the layout markup, so a right-to-left page paints right to left before
 * React hydrates. `DocumentLanguage` keeps both in step afterwards.
 */
export function documentLanguageScript(locale: AvailableLocale): string {
  return (
    `document.documentElement.lang=${JSON.stringify(locale)};` +
    `document.documentElement.dir=${JSON.stringify(textDirection(locale))};`
  );
}

/** One locale's compiled messages, as `react-intl` consumes them. */
export type LocaleMessages =
  Record<string, string> | Record<string, MessageFormatElement[]>;

export type Translations = {
  [locale in AvailableLocale]: LocaleMessages;
};

export interface LanguageProviderProps {
  locale: AvailableLocale;
  /**
   * Messages resolved on the server for the locale the tree starts in, so the
   * first render is already translated and nothing waits on a chunk. A later
   * `locale` change loads its own set.
   */
  initialMessages: { locale: AvailableLocale; messages: LocaleMessages };
  /**
   * Sets `<html lang dir>` to the locale whose messages are active. Only the
   * provider that owns the page sets it, so a nested one, such as an error
   * boundary's, leaves the page's language alone.
   */
  setsDocumentLanguage?: boolean;
  timeZone?: string;
  children: React.ReactNode;
}
